import { Skeleton } from '@mui/material';
import { isEqual } from 'lodash';
import { memo, Suspense, useMemo } from 'react';
import { lazy } from 'react';
import { GCFErrorBoundary } from 'src/components/errors/GCFErrorBoundary';
import { GCFConfigProvider } from 'src/components/gcfConfigProvider';
import { IDyTemplate } from 'src/hooks';
import { useGetProductWithVariationsComplete } from 'src/hooks/api/product/useGetAllProduct';
import { ICCAsset } from 'src/interfaces';

import {
  convertVariationMapToTemplateFormat,
  getDummySingleShelfConfig,
  getLocalTemplate,
  VariationMap,
} from './VariationTemplateMappingStep.utils';

const Section = lazy(() =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.Section,
  })),
);

const MemorizedSection = memo(
  (props: any) => {
    return <Section {...props} />;
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);

interface TemplatePreviewSectionProps {
  template: IDyTemplate;
  variationMap: VariationMap;
  assets?: ICCAsset[];
}

export const TemplatePreviewSection = memo(
  ({ template, variationMap, assets }: TemplatePreviewSectionProps) => {
    // Fetch product data for all variations
    const { data: product1 } = useGetProductWithVariationsComplete({
      productCode: variationMap[1]?.productCode || '',
      variationCode: variationMap[1]?.variationCode || '',
    });

    const { data: product2 } = useGetProductWithVariationsComplete({
      productCode: variationMap[2]?.productCode || '',
      variationCode: variationMap[2]?.variationCode || '',
    });

    const { data: product3 } = useGetProductWithVariationsComplete({
      productCode: variationMap[3]?.productCode || '',
      variationCode: variationMap[3]?.variationCode || '',
    });

    // Compute local template
    const localTemplate = useMemo(
      () => getLocalTemplate(template.configs),
      [template.configs],
    );

    // Compute preview filters
    const previewProductFilters = useMemo(() => {
      const productCodes = Array.from(
        new Set(
          Object.values(variationMap)
            .map((variation) => variation.productCode)
            .filter(Boolean),
        ),
      );

      if (productCodes.length === 0) return [];

      return [`product_code.list=${productCodes.join('|')}`];
    }, [variationMap]);

    const previewVariationFilters = useMemo(() => {
      const productCodes = Array.from(
        new Set(
          Object.values(variationMap)
            .map((variation) => variation.productCode)
            .filter(Boolean),
        ),
      );

      if (productCodes.length === 0) return [];

      return [`group_product_code.list=${productCodes.join('|')}`];
    }, [variationMap]);

    // Compute assembly components
    const assemblyComponents = useMemo(() => getDummySingleShelfConfig(), []);

    // Compute template mapping data
    const templateMappingData = useMemo(
      () =>
        convertVariationMapToTemplateFormat(
          variationMap,
          {
            product1,
            product2,
            product3,
          },
          assets,
          localTemplate,
        ),
      [assets, product1, product2, product3, variationMap, localTemplate],
    );

    return (
      <GCFErrorBoundary>
        <Suspense
          fallback={
            <Skeleton height="100%" width="100%" variant="rectangular" />
          }>
          <GCFConfigProvider
            templates={localTemplate}
            templateVariations={templateMappingData}
            productFilters={previewProductFilters}
            productVariationFilters={previewVariationFilters}
            skipProductsApiOnEmptyFilters
            overrideActiveTrue>
            <MemorizedSection
              id={0}
              assemblyComponents={assemblyComponents}
              isFromSidebar={false}
              overflowVisible={true}
            />
          </GCFConfigProvider>
        </Suspense>
      </GCFErrorBoundary>
    );
  },
);
